import React from "react"
import { Autocomplete, TextField } from "@mui/material"
import styles from "./select.module.scss"
import { Countries } from "../../enums/dropdownValues"

const getFlag = (countryName: string) => {
    try {
        const normalizedName = countryName === "United Arab Emirates" ? "UAE" : countryName
        return require(`../../assets/images/${normalizedName}.svg`)
    } catch {
        return null
    }
}

const shouldShowFlag = (countryTitle: string): boolean => {
    const normalizedTitle = countryTitle === "United Arab Emirates" ? "UAE" : countryTitle
    return Countries.some(country => country.title === normalizedTitle)
}

interface CountryOption {
    id: number
    title: string
}

interface CountrySelectFieldProps {
    label?: string
    required?: boolean
    value: CountryOption | null
    showError?: boolean
    onChange: (value: CountryOption | null) => void
    disabled?: boolean
    options?: CountryOption[]
}

const CountrySelectField: React.FC<CountrySelectFieldProps> = ({
    label = "",
    required = false,
    value,
    showError = false,
    disabled = false,
    onChange,
    options = Countries,
}) => {
    const isError = showError && required && !value?.id

    const sortedOptions = React.useMemo(() => {
        return [...options].sort((a, b) => {
            const aHasFlag = shouldShowFlag(a.title)
            const bHasFlag = shouldShowFlag(b.title)
            
            if (aHasFlag && !bHasFlag) return -1
            if (!aHasFlag && bHasFlag) return 1
            return 0
        })
    }, [options])

    return (
        <div className={styles.form_section} style={{ width: "100%" }}>
            <div className={styles.section_body} style={{ width: "100%" }}>
                <Autocomplete
                    fullWidth
                    options={sortedOptions}
                    value={value}
                    onChange={(event, newValue) => onChange(newValue)}
                    getOptionLabel={option => option?.title}
                    isOptionEqualToValue={(option, val) => option?.id === val?.id}
                    renderOption={(props, option) => {
                        const showFlag = shouldShowFlag(option.title)
                        const flagSrc = showFlag ? getFlag(option.title) : null
                        return (
                            <li {...props} key={option.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {flagSrc && <img src={flagSrc} alt={option.title} width={20} />}
                                {option.title}
                            </li>
                        )
                    }}
                    sx={{
                        ...(isError
                            ? {
                                  "&.MuiAutocomplete-root:before, & .MuiAutocomplete-root:after": {
                                      borderColor: "#F94054 !important",
                                  },
                                  "&.MuiAutocomplete-root": {
                                      border: "1px solid #F94054 !important",
                                      height: "auto",
                                  },
                              }
                            : {
                                  "&.MuiAutocomplete-root": {
                                      height: "auto",
                                  },
                              }),
                        "& .MuiInput-root:before, & .MuiInput-root:after": {
                            borderBottom: disabled ? "none !important" : undefined,
                        },
                        "& .MuiFormControl-root": {
                            height: "100%",
                        },
                    }}
                    disabled={disabled}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder={value?.id ? value?.title : "Select country"}
                            variant="standard"
                            size="small"
                            error={isError}
                            sx={{
                                "& input::placeholder": {
                                    opacity: 1,
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        {value?.id && shouldShowFlag(value.title) && getFlag(value.title) && (
                                            <img
                                                src={getFlag(value.title)}
                                                alt={value.title}
                                                width={20}
                                                style={{ marginRight: 8 }}
                                            />
                                        )}
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </div>
        </div>
    )
}

export default CountrySelectField
